// crewbit-admin/src/components/money/UnsavedChangesDialog.tsx
//
// Spec: §InvoiceAuthoring §3h. Centered overlay with 3 buttons:
//   Save as draft / Discard / Cancel.
// ConfirmDialog only supports 2 buttons (confirm + cancel); this is a
// minimum-diff sibling sharing the same chrome (z-index 70/71, 380px max
// width, animation, icon header).

import { AlertTriangle } from 'lucide-react';
import { palette, transition } from '../../styles/tokens';

interface Props {
  isOpen: boolean;
  onSaveDraft: () => void;
  onDiscard: () => void;
  onCancel: () => void;
  saving?: boolean;
}

export function UnsavedChangesDialog({
  isOpen,
  onSaveDraft,
  onDiscard,
  onCancel,
  saving,
}: Props) {
  if (!isOpen) return null;

  return (
    <>
      <div
        onClick={onCancel}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 70,
          background: 'rgba(0,0,0,0.3)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
        }}
      />
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          maxWidth: 380,
          zIndex: 71,
          background: palette.white,
          borderRadius: 16,
          boxShadow: '0 16px 64px rgba(0,0,0,0.14), 0 4px 16px rgba(0,0,0,0.08)',
          padding: 28,
          animation: 'unsavedIn 200ms ease-out',
        }}
      >
        <style>{`@keyframes unsavedIn { from { opacity: 0; transform: translate(-50%, -50%) scale(0.95); } to { opacity: 1; transform: translate(-50%, -50%) scale(1); } }`}</style>

        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 14,
            background: palette.amberWash,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 16,
          }}
        >
          <AlertTriangle size={22} color={palette.amber} strokeWidth={1.8} />
        </div>

        <div style={{ fontSize: 16, fontWeight: 700, color: palette.g800, marginBottom: 6 }}>
          Unsaved changes
        </div>
        <div style={{ fontSize: 14, color: palette.g500, lineHeight: 1.5, marginBottom: 24 }}>
          You've made changes to this invoice. Save as draft to keep working on it later, or
          discard to throw them out.
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            style={{
              padding: '9px 18px',
              borderRadius: 20,
              border: `1px solid ${palette.g200}`,
              background: palette.white,
              fontSize: 13.5,
              fontWeight: 500,
              color: palette.g600,
              cursor: saving ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              transition: `background ${transition.fast}`,
            }}
            onMouseEnter={(e) => {
              if (!saving) e.currentTarget.style.background = palette.g50;
            }}
            onMouseLeave={(e) => (e.currentTarget.style.background = palette.white)}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onDiscard}
            disabled={saving}
            style={{
              padding: '9px 18px',
              borderRadius: 20,
              border: `1px solid ${palette.red}`,
              background: palette.white,
              fontSize: 13.5,
              fontWeight: 600,
              color: palette.red,
              cursor: saving ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              transition: `background ${transition.fast}`,
            }}
            onMouseEnter={(e) => {
              if (!saving) e.currentTarget.style.background = palette.redWash;
            }}
            onMouseLeave={(e) => (e.currentTarget.style.background = palette.white)}
          >
            Discard
          </button>
          <button
            type="button"
            onClick={onSaveDraft}
            disabled={saving}
            style={{
              padding: '9px 18px',
              borderRadius: 20,
              border: 'none',
              background: saving ? palette.g300 : palette.accent,
              color: palette.white,
              fontSize: 13.5,
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              transition: `background ${transition.fast}`,
            }}
          >
            {saving ? 'Saving…' : 'Save as draft'}
          </button>
        </div>
      </div>
    </>
  );
}
